import React from 'react';
import {
  number,
  func,
  node,
  oneOf,
  object,
  string,
  bool,
} from 'prop-types';
import {
  View,
  TouchableOpacity,
  StyleSheet,
} from 'react-native';

import BodyText from '../BodyText';

import './UnderlineLink.module.scss';

export const TEXT_APPEARANCES = [
  'larger', // font-sze: 24px (pro only)
  'large', // font-size: 18px
  'normal', // font-size: 14px
  'medium', // font-size: 12px / line-height: 18px
  'small', // font-size: 12px / line-height: 14px
  'smaller', // font-size: 10px
  'legal', // font-size: 11px
  'link', // font-size: 15px
  'small-link', // font-size: 10px
  'small-link-lowercase', // font-size: 12px
];

const UnderlineLink = ({
  onPress,
  underlineWidthPercentage,
  underlineHeight,
  underlineVerticalOffset,
  tone,
  children,
  style,
  textAppearance,
  textWeight,
  testID,
  preventDefault,
  noRole,
}) => {
  const underlineStyle = {
    bottom: underlineVerticalOffset,
    height: underlineHeight,
    width: `${underlineWidthPercentage}%`,
    left: `${(100 - underlineWidthPercentage) / 2}%`,
  };

  const styles = StyleSheet.create({
    underlineStyle,
  });

  const Wrapper = typeof onPress === 'function' ? TouchableOpacity : View;

  const cancelEventThenExecuteCB = event => {
    if (event && event.preventDefault) {
      event.preventDefault();
    }

    onPress();
  };

  return (
    <Wrapper
      onPress={preventDefault ? cancelEventThenExecuteCB : onPress}
      accessibilityRole={noRole ? null : 'link'}
      style={style}
      testID={testID}
    >
      <View styleName="underline-link-component">
        <View styleName="underline-content">
          <BodyText
            styleName={`underline-text-${tone}`}
            appearance={textAppearance}
            weight={textWeight}
          >
            {children}
          </BodyText>
        </View>
        <View styleName={`underline-color-${tone}`} style={styles.underlineStyle} />
      </View>
    </Wrapper>
  );
};

UnderlineLink.propTypes = {
  /** Called when the component is pressed */
  onPress: func,
  /** The width percentage of the underline */
  underlineWidthPercentage: number,
  /** The height of the underline */
  underlineHeight: number,
  /** The vertical offset from the content of the underline */
  underlineVerticalOffset: number,
  /** Available color schemes for the underline (default: $black-in-black, bright: $punch, money:
   * green) */
  tone: oneOf([
    'default',
    'bright',
    'money',
    'blue',
    'slate',
  ]),
  children: node,
  style: object,
  textAppearance: oneOf(TEXT_APPEARANCES),
  textWeight: oneOf([
    'thin',
    'normal',
    'bold',
    'heavy',
    'medium',
    undefined,
  ]),
  testID: string,
  /** prevent default event */
  preventDefault: bool,
  noRole: bool,
};

UnderlineLink.defaultProps = {
  underlineWidthPercentage: 100,
  underlineHeight: 1.5,
  underlineVerticalOffset: -1.5,
  onPress: undefined,
  tone: 'default',
  children: undefined,
  style: {},
  textWeight: 'bold',
  textAppearance: 'normal',
  testID: null,
  preventDefault: false,
  noRole: false,
};

export default UnderlineLink;
